import { useQuery } from '@tanstack/react-query';
import {
  getSolutions,
  type GetSolutionsParams,
} from 'packages/tackle-api/src/co-sell-msft/solutions';
import useMsftVendorSettings from './useMsftVendorSettings';

/**
 * A React Query hook that fetches Microsoft solutions from the API.
 * Uses infinite cache (staleTime: Infinity) to prevent unnecessary refetches.
 * enabled by default
 * grabs the referralOrganizationID (tenantId) from the vendor settings and enables the query
 *
 * @param params - Optional query parameters to filter solutions
 * @param options - Optional configuration for the query behavior
 * @param options.enabled - When false, the query will not execute automatically
 *
 * @returns A React Query result object containing the solutions data and query state
 *
 * @example
 * // Basic usage
 * const { data } = useMsftSolutions();
 *
 * // With filters
 * const { data } = useMsftSolutions({ solutionID: 'abc123' });
 * // or
 * const { data } = useMsftSolutions({ solutionID: ['abc123', 'def456'] });
 *
 * // Disabled query
 * const { data } = useMsftSolutions(undefined, { enabled: false });
 */
const useMsftSolutions = (
  params?: Exclude<GetSolutionsParams, 'referralOrganizationID'>,
  options?: { enabled?: boolean },
) => {
  const { data: vendorSettings } = useMsftVendorSettings();
  const referralOrganizationID = vendorSettings?.tenantId;

  const query = useQuery({
    queryKey: ['solutions', referralOrganizationID, params],
    queryFn: () => getSolutions({ referralOrganizationID, ...params }),
    staleTime: Infinity,
    enabled: (options?.enabled ?? true) && !!referralOrganizationID,
  });

  return query;
};

export default useMsftSolutions;
